import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, MapPin, Gift, Search, CheckCircle, LogOut, Phone, Plus, XCircle, Clock, Users, ShoppingBag, Euro, Printer, Trash2, Mail, User, Heart } from 'lucide-react';
import PWAInstallPrompt from '../components/PWAInstallPrompt';
import { loginAdmin, logoutAdmin, checkAdminSession, setUnauthorizedHandler, getAdminToken } from '../lib/adminAuth';
import { escapeHtml } from '../lib/htmlEscape';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_hilanta-wellness/artifacts/yzd7rzdb_76b87d66-fa32-4fab-93bc-4ef8dc416573.jpg";

// Couleurs par catégorie pour les services
const getCategoryColor = (category) => {
  const colors = {
    massage: { bg: 'bg-orange-50', border: 'border-orange-200', borderSelected: 'border-orange-500', title: 'text-orange-700', price: 'text-orange-600' },
    feminin: { bg: 'bg-[#E8F0E9]', border: 'border-[#A8C5AE]', borderSelected: 'border-[#4A6D5C]', title: 'text-[#4A6D5C]', price: 'text-[#4A6D5C]' },
    naturopathie: { bg: 'bg-amber-50', border: 'border-amber-200', borderSelected: 'border-amber-500', title: 'text-amber-700', price: 'text-amber-600' },
  };
  return colors[category] || colors.massage;
};

const Accueil = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('validate'); // validate or create
  
  // Gift cards search/validate
  const [giftCardCode, setGiftCardCode] = useState('');
  const [giftCardResult, setGiftCardResult] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [validating, setValidating] = useState(false);

  // Create gift card state
  const [services, setServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [duoServices, setDuoServices] = useState({});
  const [giftCardForm, setGiftCardForm] = useState({
    buyer_name: '',
    buyer_email: '',
    buyer_phone: '',
    recipient_name: '',
    personal_message: '',
    payment_method: 'cash'
  });
  const [creatingGiftCard, setCreatingGiftCard] = useState(false);
  const [createdCard, setCreatedCard] = useState(null);
  const [phoneSuggestions, setPhoneSuggestions] = useState([]);
  const [showPhoneSuggestions, setShowPhoneSuggestions] = useState(false);

  const LOGO_PRINT_URL = `${BACKEND_URL}/api/email-assets/logo.png`;

  const lookupByPhone = async (phone) => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 5) { setPhoneSuggestions([]); return; }
    try {
      const res = await axios.get(`${API}/clients/lookup?phone=${digits}`);
      setPhoneSuggestions(res.data || []);
      setShowPhoneSuggestions(res.data && res.data.length > 0);
    } catch { setPhoneSuggestions([]); }
  };

  const applyClientSuggestion = (client) => {
    setGiftCardForm(prev => ({
      ...prev,
      buyer_name: client.name || prev.buyer_name,
      buyer_email: client.email || prev.buyer_email,
    }));
    setShowPhoneSuggestions(false);
    setPhoneSuggestions([]);
  };

  const deleteGiftCard = async (code) => {
    if (!window.confirm(`Supprimer la carte ${code} ?`)) return;
    try {
      await axios.delete(`${API}/gift-cards/${code}`);
      toast.success('Carte supprimée');
      setGiftCardResult(null);
      setGiftCardCode('');
      setCreatedCard(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const printGiftCard = (gc) => {
    const expiresDate = gc.expires_at ? new Date(gc.expires_at).toLocaleDateString('fr-FR') : 'N/A';
    // Escape all customer-supplied values before injection — see lib/htmlEscape.js
    const safeService = escapeHtml(gc.service_name || 'Soin bien-être');
    const safeCode = escapeHtml(gc.code);
    const safeExpires = escapeHtml(expiresDate);
    const safeBuyer = escapeHtml(gc.buyer_name || '');
    const safeRecipient = escapeHtml(gc.recipient_name || '');
    const leftSvg = `<svg xmlns='http://www.w3.org/2000/svg' width='60' height='240' viewBox='0 0 60 240'><path d='M5 235 Q18 195 32 150 Q46 105 55 60' stroke='#A8C5AE' stroke-width='1.2' fill='none'/><ellipse cx='12' cy='215' rx='12' ry='4' fill='#BFD4C4' transform='rotate(-50 12 215)' opacity='.7'/><ellipse cx='20' cy='192' rx='11' ry='4' fill='#A8C5AE' transform='rotate(-45 20 192)' opacity='.7'/><ellipse cx='28' cy='168' rx='12' ry='4' fill='#C0D5BF' transform='rotate(-42 28 168)' opacity='.7'/><ellipse cx='38' cy='138' rx='11' ry='3.5' fill='#A8C5AE' transform='rotate(-45 38 138)' opacity='.7'/><circle cx='8' cy='224' r='2' fill='#D4B5B0' opacity='.5'/><circle cx='16' cy='202' r='1.8' fill='#D4B5B0' opacity='.45'/></svg>`;
    const rightSvg = `<svg xmlns='http://www.w3.org/2000/svg' width='60' height='240' viewBox='0 0 60 240'><path d='M55 5 Q42 45 28 90 Q14 135 5 180' stroke='#A8C5AE' stroke-width='1.2' fill='none'/><ellipse cx='48' cy='25' rx='12' ry='4' fill='#BFD4C4' transform='rotate(50 48 25)' opacity='.7'/><ellipse cx='40' cy='48' rx='11' ry='4' fill='#A8C5AE' transform='rotate(45 40 48)' opacity='.7'/><ellipse cx='32' cy='72' rx='12' ry='4' fill='#C0D5BF' transform='rotate(42 32 72)' opacity='.7'/><ellipse cx='22' cy='102' rx='11' ry='3.5' fill='#A8C5AE' transform='rotate(45 22 102)' opacity='.7'/><circle cx='52' cy='14' r='2' fill='#D4B5B0' opacity='.5'/><circle cx='44' cy='37' r='1.8' fill='#D4B5B0' opacity='.45'/></svg>`;
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
<style>
@page { size: 20cm 9.5cm landscape; margin: 0; }
*{box-sizing:border-box;margin:0;padding:0;}
body{width:20cm;height:9.5cm;background:#fff;font-family:Arial,Helvetica,sans-serif;overflow:hidden;}
.card{width:100%;height:100%;display:flex;align-items:stretch;background:#fff;}
.deco{width:1.6cm;flex-shrink:0;display:flex;align-items:flex-end;}
.deco.right{align-items:flex-start;}
.content{flex:1;padding:0.45cm 0.3cm;display:flex;flex-direction:column;align-items:center;}
.logo-wrap{text-align:center;margin-bottom:0.12cm;}
.logo{width:1.6cm;height:1.6cm;border-radius:50%;object-fit:cover;}
.brand{font-size:7.5pt;letter-spacing:4px;font-weight:bold;color:#1A1A1A;margin-top:0.06cm;}
.title{font-size:13pt;letter-spacing:7px;font-weight:bold;color:#1A1A1A;margin:0.18cm 0 0.22cm;}
.fields{width:100%;font-size:6.8pt;}
.field{font-weight:bold;letter-spacing:1px;margin-bottom:0.13cm;color:#1A1A1A;}
.field span{font-weight:normal;}
.row{display:flex;justify-content:space-between;gap:0.3cm;}
.row .field{flex:1;}
hr{border:none;border-top:0.5pt solid #ccc;margin:0.13cm 0;}
.footer{font-size:6pt;text-align:center;color:#555;line-height:1.5;}
.italic{font-style:italic;}
.bold{font-weight:bold;color:#333;}
</style></head>
<body><div class="card">
<div class="deco"><img src="data:image/svg+xml;charset=utf-8,${encodeURIComponent(leftSvg)}" width="60" height="240"/></div>
<div class="content">
  <div class="logo-wrap"><img src="${LOGO_PRINT_URL}" class="logo"/><div class="brand">HILANTA</div></div>
  <div class="title">CARTE CADEAU</div>
  <div class="fields">
    <div class="field">PRESTATION : <span>${safeService}</span></div>
    <div class="field">NUMÉRO DE CARTE : <span style="font-family:monospace;letter-spacing:2px;">${safeCode}</span></div>
    <div class="field">VALABLE JUSQU'AU : <span>${safeExpires}</span></div>
    <div class="row">
      <div class="field">OFFERT PAR : <span>${safeBuyer}</span></div>
      <div class="field">POUR : <span>${safeRecipient}</span></div>
    </div>
  </div>
  <hr/>
  <div class="footer">
    <div class="italic">Réservation par téléphone ou sur hilanta.fr</div>
    <div>58 Avenue Maréchal foch, 13004 Marseille &nbsp;|&nbsp; Contact : 09 60 42 18 22 &nbsp;|&nbsp; la.hilanta@gmail.com</div>
    <div class="bold">Non remboursable, non échangeable</div>
  </div>
</div>
<div class="deco right"><img src="data:image/svg+xml;charset=utf-8,${encodeURIComponent(rightSvg)}" width="60" height="240"/></div>
</div></body></html>`;
    const w = window.open('', '_blank', 'width=900,height=450');
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 600);
  };
  // Auth admin : géré côté serveur (bcrypt + JWT 8h via /api/auth/admin-login)
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      await loginAdmin(password, 'accueil');
      setIsAuthenticated(true);
      toast.success('Bienvenue à l\'accueil Hilanta !');
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        toast.error('Trop de tentatives. Réessayez dans 5 minutes.');
      } else if (status === 401) {
        toast.error('Mot de passe incorrect');
      } else {
        toast.error('Erreur de connexion. Vérifiez votre réseau.');
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    await logoutAdmin();
    setIsAuthenticated(false);
    setPassword('');
    toast.info('Déconnexion réussie');
  };

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setIsAuthenticated(false);
      setPassword('');
      toast.error('Session expirée, veuillez vous reconnecter');
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (getAdminToken()) {
        const ok = await checkAdminSession();
        if (!cancelled && ok) setIsAuthenticated(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch services when creating gift card
  useEffect(() => {
    if (isAuthenticated && activeTab === 'create' && services.length === 0) {
      fetchServices();
    }
  }, [isAuthenticated, activeTab]);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  // Search gift card
  const searchGiftCard = async () => {
    if (!giftCardCode || giftCardCode.length < 3) {
      toast.error('Entrez un code valide');
      return;
    }
    
    setSearchLoading(true);
    setGiftCardResult(null);
    
    try {
      const response = await axios.get(`${API}/gift-cards/check/${giftCardCode.toUpperCase()}`);
      setGiftCardResult(response.data);
    } catch (error) {
      toast.error('Carte cadeau non trouvée');
      setGiftCardResult(null);
    } finally {
      setSearchLoading(false);
    }
  };

  // Mark gift card as used
  const markAsUsed = async () => {
    if (!giftCardResult) return;
    
    setValidating(true);
    try {
      await axios.post(`${API}/gift-cards/${giftCardResult.code}/use`);
      toast.success('Carte cadeau validée !');
      setGiftCardResult({ ...giftCardResult, is_used: true, status_display: 'Utilisée' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setValidating(false);
    }
  };

  // Gift card creation functions
  const canHaveDuo = (service) => {
    return service.category === 'massage' || service.name.toLowerCase().includes('femme enceinte');
  };

  const toggleService = (service) => {
    setSelectedServices(prev => {
      const isSelected = prev.find(s => s.id === service.id);
      if (isSelected) {
        setDuoServices(d => {
          const newDuo = { ...d };
          delete newDuo[service.id];
          return newDuo;
        });
        return prev.filter(s => s.id !== service.id);
      } else {
        return [...prev, service];
      }
    });
  };

  const toggleDuo = (serviceId, e) => {
    e.stopPropagation();
    setDuoServices(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };

  const isServiceSelected = (serviceId) => {
    return selectedServices.some(s => s.id === serviceId);
  };

  const getTotalPrice = () => {
    return selectedServices.reduce((total, service) => {
      const isDuo = duoServices[service.id] || false;
      return total + (isDuo ? service.price * 2 : service.price);
    }, 0);
  };

  const createGiftCardOnSite = async (e) => {
    e.preventDefault();
    
    if (selectedServices.length === 0) {
      toast.error('Sélectionnez au moins un soin');
      return;
    }
    
    if (!giftCardForm.buyer_name) {
      toast.error('Entrez le nom du client');
      return;
    }

    setCreatingGiftCard(true);
    try {
      const totalPrice = getTotalPrice();
      
      const servicesForCard = selectedServices.map(service => {
        const isDuo = duoServices[service.id] || false;
        return {
          id: service.id,
          name: isDuo ? `${service.name} (DUO)` : service.name,
          duration: service.duration,
          price: isDuo ? service.price * 2 : service.price,
          is_duo: isDuo
        };
      });
      
      const serviceNames = servicesForCard.map(s => s.name).join(' + ');
      
      const response = await axios.post(`${API}/gift-cards/create-onsite`, {
        amount: totalPrice,
        services: servicesForCard,
        service_name: serviceNames,
        buyer_name: giftCardForm.buyer_name,
        buyer_email: giftCardForm.buyer_email || '',
        buyer_phone: giftCardForm.buyer_phone || '',
        recipient_name: giftCardForm.recipient_name || '',
        personal_message: giftCardForm.personal_message,
        payment_method: giftCardForm.payment_method,
        message: giftCardForm.personal_message || `Carte cadeau : ${serviceNames}`
      });
      
      toast.success(`✅ Carte créée ! Code : ${response.data.code}`, { duration: 10000 });
      
      // Store created card for display with full coordinates
      setCreatedCard({
        code: response.data.code,
        service_name: serviceNames,
        buyer_name: giftCardForm.buyer_name,
        buyer_phone: giftCardForm.buyer_phone || '',
        buyer_email: giftCardForm.buyer_email || '',
        recipient_name: giftCardForm.recipient_name || '',
        amount: totalPrice,
        expires_at: new Date(Date.now() + 365*24*60*60*1000).toISOString(),
      });
      
      // Reset form
      setSelectedServices([]);
      setDuoServices({});
      setGiftCardForm({
        buyer_name: '',
        buyer_email: '',
        buyer_phone: '',
        recipient_name: '',
        personal_message: '',
        payment_method: 'cash'
      });
      
    } catch (error) {
      console.error('Error creating gift card:', error);
      toast.error('Erreur lors de la création');
    } finally {
      setCreatingGiftCard(false);
    }
  };

  // Login page
  if (!isAuthenticated) {
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
          <title>Accueil - Hilanta</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center p-4" data-testid="accueil-login">
          <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8">
            <div className="text-center mb-8">
              <img
                src={LOGO_URL}
                alt="Hilanta"
                className="w-20 h-20 mx-auto mb-4 rounded-full object-cover"
                style={{ border: '3px solid #C4A246' }}
              />
              <h1 className="font-heading text-3xl text-warm-brown tracking-wide">Accueil Hilanta</h1>
              <p className="text-warm-brown-light text-xs mt-1 tracking-widest uppercase">Espace personnel</p>
            </div>
            
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="password" className="text-gray-700">Mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mot de passe"
                  className="mt-1 rounded-xl"
                  data-testid="accueil-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loginLoading || password.length < 4}
                className="w-full bg-terracotta hover:bg-terracotta-600 text-white rounded-xl py-6 text-lg"
                data-testid="accueil-login-btn"
              >
                {loginLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                ) : (
                  <Lock className="mr-2" size={20} />
                )}
                Connexion
              </Button>
            </form>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Accueil - Hilanta</title>
      </Helmet>
      
      <div className="min-h-screen py-6 px-4" data-testid="accueil-dashboard">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <img
                src={LOGO_URL}
                alt="Hilanta"
                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                style={{ border: '2px solid #C4A246' }}
              />
              <div>
                <h1 className="font-heading text-xl text-warm-brown tracking-wide">Accueil Hilanta</h1>
                <p className="text-warm-brown-light text-xs tracking-widest uppercase">Cartes cadeaux</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <PWAInstallPrompt pageType="accueil" />
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="border-red-200 text-red-600 hover:bg-red-50 rounded-xl"
              >
                <LogOut size={16} />
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setActiveTab('validate')}
              className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                activeTab === 'validate'
                  ? 'bg-warm-brown text-white'
                  : 'bg-white text-warm-brown hover:bg-sand'
              }`}
            >
              <Search size={18} />
              Valider une carte
            </button>
            <button
              onClick={() => setActiveTab('create')}
              className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                activeTab === 'create'
                  ? 'bg-terracotta text-white'
                  : 'bg-white text-warm-brown hover:bg-sand'
              }`}
            >
              <Plus size={18} />
              Créer une carte
            </button>
          </div>

          {/* Validate Tab */}
          {activeTab === 'validate' && (
            <div className="bg-white rounded-3xl shadow-lg p-6">
              <div className="text-center mb-6">
                <div className="mx-auto w-14 h-14 bg-sand rounded-full flex items-center justify-center mb-3">
                  <Gift className="text-terracotta" size={28} />
                </div>
                <h2 className="font-heading text-xl text-warm-brown">Valider une carte cadeau</h2>
                <p className="text-warm-brown-light text-sm mt-1">Entrez le code de la carte du client</p>
              </div>

              {/* Search */}
              <div className="flex gap-2 mb-6">
                <Input
                  value={giftCardCode}
                  onChange={(e) => setGiftCardCode(e.target.value.toUpperCase())}
                  placeholder="CODE DE LA CARTE"
                  className="flex-1 rounded-xl text-center font-mono text-lg tracking-widest uppercase py-5"
                  maxLength={20}
                  onKeyPress={(e) => e.key === 'Enter' && searchGiftCard()}
                />
                <Button
                  onClick={searchGiftCard}
                  disabled={searchLoading || giftCardCode.length < 3}
                  className="bg-warm-brown hover:bg-warm-brown/90 text-white rounded-xl px-6"
                >
                  {searchLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <Search size={20} />
                  )}
                </Button>
              </div>

              {/* Result */}
              {giftCardResult && (
                <div className={`p-5 rounded-2xl border-2 ${
                  giftCardResult.is_used 
                    ? 'bg-gray-50 border-gray-200' 
                    : giftCardResult.is_expired 
                      ? 'bg-red-50 border-red-200'
                      : 'bg-green-50 border-green-200'
                }`}>
                  <div className="text-center mb-4">
                    <span className={`inline-block px-4 py-2 rounded-full text-lg font-mono font-bold ${
                      giftCardResult.is_used 
                        ? 'bg-gray-200 text-gray-600' 
                        : giftCardResult.is_expired
                          ? 'bg-red-200 text-red-700'
                          : 'bg-amber-200 text-amber-800'
                    }`}>
                      {giftCardResult.code}
                    </span>
                  </div>
                  
                  <p className={`text-xl font-bold text-center mb-4 ${
                    giftCardResult.is_used ? 'text-gray-600' : 
                    giftCardResult.is_expired ? 'text-red-600' : 'text-green-600'
                  }`}>
                    {giftCardResult.is_used ? '❌ DÉJÀ UTILISÉE' : 
                     giftCardResult.is_expired ? '⏰ EXPIRÉE' : '✅ VALIDE'}
                  </p>
                  
                  <div className="bg-white rounded-xl p-4 text-sm space-y-2.5">
                    {/* Soin + valeur */}
                    <div className="pb-2.5 border-b border-gray-100">
                      <p className="text-gray-700"><strong>Soin :</strong> {giftCardResult.service_name || 'Non spécifié'}</p>
                      <p className="text-gray-700"><strong>Valeur :</strong> <span className="font-bold text-amber-700">{giftCardResult.amount}€</span></p>
                      <p className="text-gray-400 text-xs mt-1">
                        Expire le : {giftCardResult.expires_at ? new Date(giftCardResult.expires_at).toLocaleDateString('fr-FR') : 'Non défini'}
                      </p>
                    </div>
                    {/* Coordonnées client */}
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-1.5 font-medium">Coordonnées client</p>
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-2 text-gray-700">
                          <User size={13} className="text-gray-400 flex-shrink-0" />
                          <span><strong>Offert par :</strong> {giftCardResult.buyer_name || 'Anonyme'}</span>
                        </div>
                        {giftCardResult.recipient_name && (
                          <div className="flex items-center gap-2 text-gray-700">
                            <Heart size={13} className="text-pink-400 flex-shrink-0" />
                            <span><strong>Pour :</strong> {giftCardResult.recipient_name}</span>
                          </div>
                        )}
                        {giftCardResult.buyer_phone && (
                          <div className="flex items-center gap-2 text-gray-700">
                            <Phone size={13} className="text-green-600 flex-shrink-0" />
                            <a href={`tel:${giftCardResult.buyer_phone}`} className="font-medium hover:underline">
                              {giftCardResult.buyer_phone}
                            </a>
                          </div>
                        )}
                        {giftCardResult.buyer_email && (
                          <div className="flex items-center gap-2 text-gray-700">
                            <Mail size={13} className="text-blue-500 flex-shrink-0" />
                            <a href={`mailto:${giftCardResult.buyer_email}`} className="hover:underline truncate">
                              {giftCardResult.buyer_email}
                            </a>
                          </div>
                        )}
                        {!giftCardResult.buyer_phone && !giftCardResult.buyer_email && (
                          <p className="text-xs text-gray-400 italic">Pas de coordonnées enregistrées</p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {!giftCardResult.is_used && !giftCardResult.is_expired && (
                    <Button
                      onClick={markAsUsed}
                      disabled={validating}
                      className="w-full mt-4 bg-terracotta hover:bg-terracotta/90 text-white rounded-xl py-5 text-lg"
                    >
                      {validating ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      ) : (
                        <CheckCircle className="mr-2" size={20} />
                      )}
                      Valider cette carte
                    </Button>
                  )}
                  <div className="flex gap-2 mt-3">
                    <Button
                      onClick={() => printGiftCard(giftCardResult)}
                      variant="outline"
                      className="flex-1 border-warm-brown/30 text-warm-brown rounded-xl flex items-center justify-center gap-2"
                    >
                      <Printer size={16} />
                      Imprimer
                    </Button>
                    {(giftCardResult.is_used || giftCardResult.is_expired) && (
                      <Button
                        onClick={() => deleteGiftCard(giftCardResult.code)}
                        variant="outline"
                        className="border-red-200 text-red-600 hover:bg-red-50 rounded-xl flex items-center justify-center gap-2 px-4"
                      >
                        <Trash2 size={16} />
                        Supprimer
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Create Tab */}
          {activeTab === 'create' && (
            <div className="bg-white rounded-3xl shadow-lg p-6">
              <div className="text-center mb-6">
                <div className="mx-auto w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mb-3">
                  <ShoppingBag className="text-amber-600" size={28} />
                </div>
                <h2 className="font-heading text-xl text-gray-800">Créer une carte cadeau</h2>
                <p className="text-gray-500 text-sm mt-1">Pour un client sur place</p>
              </div>

              <form onSubmit={createGiftCardOnSite} className="space-y-5">
                {/* Services Selection */}
                <div>
                  <Label className="text-gray-700 font-medium mb-2 block text-sm">
                    Soins à offrir
                  </Label>
                  <div className="grid grid-cols-1 gap-2 max-h-[250px] overflow-y-auto p-1">
                    {services.filter(s => !s.name.includes('Silhouette')).map((service) => {
                      const colors = getCategoryColor(service.category);
                      const isSelected = isServiceSelected(service.id);
                      const showDuoOption = canHaveDuo(service);
                      const isDuo = duoServices[service.id] || false;
                      const displayPrice = isDuo ? service.price * 2 : service.price;
                      
                      return (
                        <div
                          key={service.id}
                          onClick={() => toggleService(service)}
                          className={`p-3 rounded-xl ${colors.bg} border-2 ${
                            isSelected ? colors.borderSelected : colors.border
                          } cursor-pointer transition-all`}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                              isSelected ? 'bg-amber-500 border-amber-500' : 'border-gray-300 bg-white'
                            }`}>
                              {isSelected && <CheckCircle size={10} className="text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium text-sm ${colors.title} truncate`}>
                                {service.name}
                                {isSelected && isDuo && <span className="ml-1">(DUO)</span>}
                              </p>
                              <p className="text-xs text-gray-500">{service.duration} min</p>
                            </div>
                            <span className={`font-bold text-sm ${colors.price}`}>{displayPrice}€</span>
                          </div>
                          
                          {isSelected && showDuoOption && (
                            <button
                              type="button"
                              onClick={(e) => toggleDuo(service.id, e)}
                              className={`mt-2 w-full flex items-center justify-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                                isDuo 
                                  ? 'bg-amber-500 text-white' 
                                  : 'bg-amber-100 text-amber-700 border border-amber-300'
                              }`}
                            >
                              <Users size={10} />
                              DUO x2
                              {isDuo && <CheckCircle size={10} />}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {selectedServices.length > 0 && (
                    <div className="mt-3 p-3 bg-amber-50 rounded-xl border border-amber-200">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-amber-800">
                          {selectedServices.length} soin{selectedServices.length > 1 ? 's' : ''} sélectionné{selectedServices.length > 1 ? 's' : ''}
                        </span>
                        <span className="text-lg font-bold text-amber-800 flex items-center gap-1">
                          <Euro size={16} />
                          {getTotalPrice()}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Client Info */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Phone first for lookup */}
                  <div className="col-span-2">
                    <Label className="text-gray-700 text-sm">Téléphone (pour retrouver un client)</Label>
                    <div className="relative">
                      <Input
                        value={giftCardForm.buyer_phone || ''}
                        onChange={(e) => {
                          setGiftCardForm({ ...giftCardForm, buyer_phone: e.target.value });
                          lookupByPhone(e.target.value);
                        }}
                        onBlur={() => setTimeout(() => setShowPhoneSuggestions(false), 200)}
                        placeholder="06 12 34 56 78"
                        className="mt-1 rounded-xl"
                      />
                      {showPhoneSuggestions && phoneSuggestions.length > 0 && (
                        <div className="absolute z-20 top-full left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-lg mt-1 overflow-hidden">
                          {phoneSuggestions.map((c, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => applyClientSuggestion(c)}
                              className="w-full text-left px-4 py-3 hover:bg-sand transition-colors border-b border-gray-100 last:border-0"
                            >
                              <p className="font-medium text-warm-brown text-sm">{c.name}</p>
                              <p className="text-xs text-warm-brown-light">{c.phone}{c.email ? ` · ${c.email}` : ''}</p>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-gray-700 text-sm">Offert par (acheteur) *</Label>
                    <Input
                      value={giftCardForm.buyer_name}
                      onChange={(e) => setGiftCardForm({ ...giftCardForm, buyer_name: e.target.value })}
                      placeholder="Nom de l'acheteur"
                      className="mt-1 rounded-xl"
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-gray-700 text-sm">Pour (bénéficiaire)</Label>
                    <Input
                      value={giftCardForm.recipient_name}
                      onChange={(e) => setGiftCardForm({ ...giftCardForm, recipient_name: e.target.value })}
                      placeholder="Nom de la personne à qui offrir"
                      className="mt-1 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700 text-sm">Email</Label>
                    <Input
                      type="email"
                      value={giftCardForm.buyer_email}
                      onChange={(e) => setGiftCardForm({ ...giftCardForm, buyer_email: e.target.value })}
                      placeholder="email@..."
                      className="mt-1 rounded-xl text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700 text-sm">Paiement</Label>
                    <select
                      value={giftCardForm.payment_method}
                      onChange={(e) => setGiftCardForm({ ...giftCardForm, payment_method: e.target.value })}
                      className="w-full mt-1 px-3 py-2 rounded-xl border border-gray-200 text-sm"
                    >
                      <option value="cash">Espèces</option>
                      <option value="card">CB</option>
                      <option value="transfer">Virement</option>
                    </select>
                  </div>
                  {/* Petit mot personnalisé */}
                  <div className="col-span-2">
                    <Label className="text-gray-700 text-sm">Petit mot personnalisé (optionnel)</Label>
                    <textarea
                      value={giftCardForm.personal_message}
                      onChange={(e) => setGiftCardForm({ ...giftCardForm, personal_message: e.target.value })}
                      placeholder="Message à inscrire sur la carte cadeau..."
                      className="w-full mt-1 rounded-xl border border-gray-200 p-3 min-h-[70px] text-sm"
                      maxLength={300}
                    />
                  </div>
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  disabled={creatingGiftCard || selectedServices.length === 0}
                  className="w-full bg-terracotta hover:bg-terracotta/90 text-white rounded-xl py-5 text-lg"
                >
                  {creatingGiftCard ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  ) : (
                    <Gift className="mr-2" size={20} />
                  )}
                  Créer la carte {selectedServices.length > 0 && `(${getTotalPrice()}€)`}
                </Button>
              </form>

              {/* After creation: show coordinates + print + delete */}
              {createdCard && (
                <div className="mt-4 bg-white rounded-2xl shadow-lg p-5 border-2 border-terracotta/20">
                  <p className="text-xs text-warm-brown-light uppercase tracking-wide mb-3 font-medium">Carte créée avec succès</p>
                  
                  {/* Code */}
                  <div className="text-center mb-4">
                    <span className="font-mono text-2xl font-bold text-terracotta bg-terracotta/10 px-4 py-2 rounded-xl">
                      {createdCard.code}
                    </span>
                  </div>

                  {/* Soin + montant */}
                  <div className="bg-sand rounded-xl p-3 mb-3 text-sm space-y-1">
                    <p className="text-warm-brown"><strong>Soin :</strong> {createdCard.service_name}</p>
                    <p className="text-warm-brown"><strong>Montant :</strong> <span className="font-bold text-amber-700">{createdCard.amount}€</span></p>
                    <p className="text-warm-brown-light text-xs">
                      Expire le : {new Date(createdCard.expires_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>

                  {/* Coordonnées client */}
                  <div className="bg-sand rounded-xl p-3 mb-4 text-sm">
                    <p className="text-xs text-warm-brown-light uppercase tracking-wide mb-2 font-medium">Coordonnées client</p>
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2 text-warm-brown">
                        <User size={13} className="text-warm-brown-light flex-shrink-0" />
                        <span><strong>Offert par :</strong> {createdCard.buyer_name}</span>
                      </div>
                      {createdCard.recipient_name && (
                        <div className="flex items-center gap-2 text-warm-brown">
                          <Heart size={13} className="text-pink-400 flex-shrink-0" />
                          <span><strong>Pour :</strong> {createdCard.recipient_name}</span>
                        </div>
                      )}
                      {createdCard.buyer_phone && (
                        <div className="flex items-center gap-2 text-warm-brown">
                          <Phone size={13} className="text-green-600 flex-shrink-0" />
                          <a href={`tel:${createdCard.buyer_phone}`} className="font-medium hover:underline">
                            {createdCard.buyer_phone}
                          </a>
                        </div>
                      )}
                      {createdCard.buyer_email && (
                        <div className="flex items-center gap-2 text-warm-brown">
                          <Mail size={13} className="text-blue-500 flex-shrink-0" />
                          <span className="truncate">{createdCard.buyer_email}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      onClick={() => printGiftCard(createdCard)}
                      className="flex-1 bg-warm-brown hover:bg-warm-brown/90 text-white rounded-xl flex items-center justify-center gap-2"
                    >
                      <Printer size={16} />
                      Imprimer la carte
                    </Button>
                    <Button
                      onClick={() => setCreatedCard(null)}
                      variant="outline"
                      className="rounded-xl border-warm-brown/30 text-warm-brown"
                    >
                      Nouvelle carte
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Contact */}
          <div className="mt-6 text-center">
            <a href="tel:+33960421822" className="flex items-center justify-center gap-2 text-warm-brown-light font-medium text-sm">
              <Phone size={16} />
              09 60 42 18 22
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Accueil;
